import React from "react";
import { Check, ExternalLink, ShieldCheck } from "lucide-react";

/**
 * CertificateBlock — the certificate rendered as a designed document artifact.
 *
 * The verification link IS the proposition of this whole site: the founder
 * asked that this be the best-designed component we ship, and worth
 * screenshotting to send to an astrologer.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  [ISSUED BY]                                    ┌────────┐│
 *   │  Gemological Institute of America              │  ✓ Lab  ││
 *   │  A.k.a. GIA — an independent laboratory        │        ││
 *   │                                                └────────┘│
 *   │  ────────────────────────────────────────────────         │
 *   │  REPORT NUMBER                                            │
 *   │  2 2 0 2  0 6 5  7 5 5                                    │
 *   │                                                            │
 *   │  [Verify on ssef.ch →]  ← unmistakable primary action     │
 *   │  Opens on the laboratory's own website. We do not host    │
 *   │  the report on our domain — you check them, not us.       │
 *   └────────────────────────────────────────────────────────────┘
 */
export function CertificateBlock({ certificate, className = "", testId = "certificate-block" }) {
  if (!certificate || !certificate.is_third_party) {
    return (
      <div
        data-testid={`${testId}-self-described`}
        className={`border border-rule bg-surface p-5 ${className}`}
      >
        <p className="ui text-[10px] font-medium uppercase tracking-[0.22em] text-dim">
          No third-party report
        </p>
        <p className="mt-2 text-[14.5px] leading-[1.65] text-ink"
           style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
          This item does not carry an independent laboratory report — here is
          exactly what we can and cannot tell you about it, on this page.
        </p>
      </div>
    );
  }
  const host = new URL(certificate.verification_url).hostname.replace("www.", "");
  return (
    <section
      data-testid={testId}
      className={`certificate-block ${className}`}
      aria-label={`${certificate.lab} report ${certificate.report_number}`}
    >
      <div className="certificate-block__head">
        <div>
          <p className="ui text-[10px] font-semibold uppercase tracking-[0.22em] text-verify">
            Issued by an independent laboratory
          </p>
          <p className="certificate-block__lab" data-testid={`${testId}-lab`}>
            {certificate.lab_name || certificate.lab}
          </p>
          {certificate.lab_name && (
            <p className="ui mt-0.5 text-[11.5px] text-dim">
              A.k.a. <span className="font-medium text-ink">{certificate.lab}</span> —
              on our allow-list of accepted laboratories.
            </p>
          )}
        </div>
        <div className="certificate-block__stamp" aria-hidden="true">
          <ShieldCheck className="h-5 w-5 text-verify" />
          <span className="ui mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-verify">
            Lab-verified
          </span>
        </div>
      </div>

      <div className="certificate-block__rule" aria-hidden="true" />

      <p className="ui text-[10px] font-semibold uppercase tracking-[0.22em] text-dim">
        Report number
      </p>
      <p
        className="certificate-block__num"
        data-testid={`${testId}-number`}
        title={`Report number ${certificate.report_number}`}
      >
        {certificate.report_number}
      </p>
      {certificate.report_date && (
        <p className="ui mt-2 text-[11px] text-dim">
          Issued {certificate.report_date}
        </p>
      )}

      <a
        href={certificate.verification_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testId}-verify`}
        className="certificate-block__cta"
      >
        <Check className="h-4 w-4" aria-hidden="true" />
        <span>Verify on {host}</span>
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>

      <p className="ui mt-3 text-[11px] leading-[1.6] text-dim">
        Opens on {host} in a new tab. We do not host the report — you check the
        laboratory's own site, not ours.
      </p>
    </section>
  );
}
